import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import type { AccountTypeFlags, AuthIntent, OpenUserDetails } from '../types';

interface AuthStore {
  // Flow state — set by one screen, read by the next
  intent: AuthIntent | null;
  username: string | null;
  isEmailLogin: boolean;
  openUserDetails: OpenUserDetails | null;
  otp: string | null; // stored after OTP entry, used by pin-reset flow
  accountFlags: AccountTypeFlags; // captured from URL params on login, persisted across screens

  // Setters
  setIntent: (intent: AuthIntent) => void;
  setUsername: (username: string, isEmail: boolean) => void;
  setOpenUserDetails: (data: OpenUserDetails) => void;
  setOtp: (otp: string) => void;
  setAccountFlags: (flags: AccountTypeFlags) => void;
  hydrateFromStorage: () => void;

  // Cleanup
  clearAuthFlow: () => void;
}

const defaultAccountFlags: AccountTypeFlags = {
  is_mlm: true,
  is_direct_agent: false,
  is_inspection_garage: false,
  is_business_manager: false,
};

/**
 * Hydrate initial state from localStorage.
 * Legacy screens (Otp, etc.) write auth state to
 * localStorage before navigating to new-module routes (/otp, /create-passcode).
 * Without this hydration the Zustand store starts empty and those screens break.
 */
function hydrateFromLegacyStorage() {
  try {
    const intent = localStorage.getItem('auth_intent') as AuthIntent | null;
    const username = localStorage.getItem('auth_username') || null;
    const isEmailLogin = localStorage.getItem('auth_emailLogin') === 'true';
    const otp = localStorage.getItem('auth_otp') || null;

    let openUserDetails: OpenUserDetails | null = null;
    const openUserRaw = localStorage.getItem('auth_openUser');
    if (openUserRaw) {
      try {
        openUserDetails = JSON.parse(openUserRaw);
      } catch {
        // ignore malformed JSON
      }
    }

    return {
      intent,
      username,
      isEmailLogin,
      openUserDetails,
      otp,
      accountFlags: defaultAccountFlags,
    };
  } catch {
    return {
      intent: null as AuthIntent | null,
      username: null as string | null,
      isEmailLogin: false,
      openUserDetails: null as OpenUserDetails | null,
      otp: null as string | null,
      accountFlags: defaultAccountFlags,
    };
  }
}

const initialFlowState = hydrateFromLegacyStorage();

/**
 * Bridge: also write to localStorage so the legacy Otp.tsx (which reads
 * from localStorage.auth_*) works if the user somehow ends up on the
 * old /otp route instead of /auth/otp.
 */
function syncToLegacyStorage(
  state: Partial<{
    intent: AuthIntent | null;
    username: string | null;
    isEmailLogin: boolean;
    openUserDetails: OpenUserDetails | null;
    otp: string | null;
  }>,
) {
  try {
    if (state.intent !== undefined) {
      if (state.intent) localStorage.setItem('auth_intent', state.intent);
      else localStorage.removeItem('auth_intent');
    }
    if (state.username !== undefined) {
      if (state.username) {
        localStorage.setItem('auth_username', state.username);
        localStorage.setItem('auth_phone', state.username);
      } else {
        localStorage.removeItem('auth_username');
        localStorage.removeItem('auth_phone');
      }
    }
    if (state.isEmailLogin !== undefined) {
      localStorage.setItem('auth_emailLogin', String(state.isEmailLogin));
      localStorage.setItem('auth_emailOtp', String(state.isEmailLogin));
    }
    if (state.openUserDetails !== undefined) {
      if (state.openUserDetails) {
        localStorage.setItem('auth_openUser', JSON.stringify(state.openUserDetails));
      } else {
        localStorage.removeItem('auth_openUser');
      }
    }
    if (state.otp !== undefined) {
      if (state.otp) localStorage.setItem('auth_otp', state.otp);
      else localStorage.removeItem('auth_otp');
    }
  } catch {
    // localStorage may be full or disabled — non-critical
  }
}

export const useAuthStore = create<AuthStore>()(
  devtools(
    (set) => ({
      ...initialFlowState,

      setIntent: (intent) => {
        syncToLegacyStorage({ intent });
        set({ intent }, false, 'setIntent');
      },

      setUsername: (username, isEmail) => {
        syncToLegacyStorage({ username, isEmailLogin: isEmail });
        set({ username, isEmailLogin: isEmail }, false, 'setUsername');
      },

      setOpenUserDetails: (data) => {
        syncToLegacyStorage({ openUserDetails: data });
        set({ openUserDetails: data }, false, 'setOpenUserDetails');
      },

      setOtp: (otp) => {
        syncToLegacyStorage({ otp });
        set({ otp }, false, 'setOtp');
      },

      setAccountFlags: (flags) => {
        set({ accountFlags: flags }, false, 'setAccountFlags');
      },

      /**
       * Re-read localStorage and populate store if current state is empty.
       * Called by screens that may be reached via legacy navigation
       * (legacy writes to localStorage, then navigates to new-module routes).
       */
      hydrateFromStorage: () => {
        const state = useAuthStore.getState();
        if (state.username && state.intent) return; // already populated

        const hydrated = hydrateFromLegacyStorage();
        if (hydrated.username && hydrated.intent) {
          set(hydrated, false, 'hydrateFromStorage');
        }
      },

      clearAuthFlow: () => {
        const emptyState = {
          intent: null as AuthIntent | null,
          username: null as string | null,
          isEmailLogin: false,
          openUserDetails: null as OpenUserDetails | null,
          otp: null as string | null,
          accountFlags: defaultAccountFlags,
        };
        syncToLegacyStorage(emptyState);
        set(emptyState, false, 'clearAuthFlow');
      },
    }),
    { name: 'auth-store' },
  ),
);
